import { sha256 } from "@noble/hashes/sha2"
import { toError } from "@src/utils/errors"
import { saveCredential } from "./webAuthnCredentialService"
import {
  type WebauthnCredential,
  type WebauthnCredentialWithPrf,
  type WebauthnPrfResult,
  createNew,
} from "./webauthnService"

export const WEBAUTHN_PRF_REGISTRATION_SALT = sha256(
  new TextEncoder().encode("near.com/webauthn/prf-registration/v1")
)

type CreatePersistedCredentialOptions = {
  userName?: string
  prf?: Uint8Array
  allowPrfFallback?: boolean
  signal?: AbortSignal
}

type PersistedCredentialResult = {
  credential: WebauthnCredential
  prf?: WebauthnPrfResult
}

function isNotSupportedError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "NotSupportedError"
}

function shouldRetryWithoutPrf(error: unknown): boolean {
  // Only retry without PRF when the browser explicitly reports PRF as unsupported.
  // Other DOMExceptions (timeout, abort, hardware error, security policy) must surface —
  // silently downgrading to a non-PRF passkey weakens long-term security guarantees and
  // hides real failures from the user.
  return isNotSupportedError(error)
}

function withPersistenceFailedCode(error: unknown): Error & {
  code: "persistence_failed"
} {
  const cause = toError(error)
  return Object.assign(new Error(cause.message, { cause }), {
    code: "persistence_failed" as const,
  })
}

export async function createPersistedCredential(
  passkeyName?: string,
  options?: CreatePersistedCredentialOptions
): Promise<PersistedCredentialResult> {
  const prf = options?.prf ?? WEBAUTHN_PRF_REGISTRATION_SALT

  try {
    const credential = await createNew(passkeyName, {
      prf,
      signal: options?.signal,
      userName: options?.userName,
    })
    const persisted = stripPrf(credential)
    await saveOrThrow(persisted)
    return { credential: persisted, prf: credential.prf }
  } catch (error) {
    if (!options?.allowPrfFallback || !shouldRetryWithoutPrf(error)) {
      throw error
    }

    const credential = await createNew(passkeyName, {
      signal: options.signal,
      userName: options.userName,
    })
    await saveOrThrow(credential)
    return { credential }
  }
}

async function saveOrThrow(credential: WebauthnCredential): Promise<void> {
  try {
    await saveCredential(credential)
  } catch (error) {
    throw withPersistenceFailedCode(error)
  }
}

function stripPrf(credential: WebauthnCredentialWithPrf): WebauthnCredential {
  return {
    rawId: credential.rawId,
    publicKey: credential.publicKey,
    ...(credential.hostname != null ? { hostname: credential.hostname } : {}),
  }
}
